/**
 * The compact layout's ≡ menu: the menu bar's menus (the same `useMenus`
 * list, so both layouts offer the same items), shown together under their
 * titles in one dropdown, since a phone has no room for a row of titles.
 */
import { useCallback, useRef, useState } from "react";
import { useMenus } from "../MenuBar";
import type { MenuItems } from "../ShellFrame";
import { MenuIcon } from "../../ui/icons";
import { MenuPopup } from "../../ui/Menu";
import { useDismissOnOutsideClick } from "../../ui/useDismissOnOutsideClick";

export function CompactMenu({ menuItems }: { menuItems: MenuItems }) {
  const menus = useMenus(menuItems);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useDismissOnOutsideClick(ref, open, close);

  return (
    <div
      className="kn-compact-menu"
      ref={ref}
      onKeyDown={(event) => {
        if (event.key === "Escape") close();
      }}
    >
      <button
        type="button"
        className="kn-compact__icon"
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <MenuIcon size={18} />
      </button>
      {open && (
        <div className="kn-compact-menu__sheet">
          {menus.map((menu, index) => (
            <section key={menu.label} className="kn-compact-menu__section">
              <h2 className="kn-compact-menu__title">{menu.label}</h2>
              <MenuPopup
                label={menu.label}
                items={menu.items}
                onClose={close}
                // Only the first list takes focus, so arrow keys start at the top.
                autoFocus={index === 0}
                className="kn-compact-menu__list"
              />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
